import { useMemo, useRef, useState, useEffect } from 'react';
import { Box, Typography, Stack, IconButton, Grid } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useLanguage } from '../../../utils/i18n';
import { getWpTimeEpoch } from '../../../utils/mapTripUtils';

const formatTime = (epochMs) => {
  if (!epochMs) return '';
  const d = new Date(epochMs);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const formatDateOnly = (epochMs) => {
  if (!epochMs) return '';

  const d = new Date(epochMs);

  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  const day = String(d.getDate()).padStart(2, '0');
  const month = months[d.getMonth()];
  const year = String(d.getFullYear()).slice(-2);
  return `${day} ${month} ${year}`;
};
const formatDateTimeFull = (epochMs) => {
  if (!epochMs) return '';
  const d = new Date(epochMs);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const day = String(d.getDate()).padStart(2, '0');
  const month = months[d.getMonth()];
  const year = String(d.getFullYear()).slice(-2);
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  const sec = String(d.getSeconds()).padStart(2, '0');

  return `${day} ${month} ${year} ${hours}:${minutes}:${sec}`;
};
const formatDateOnlyGan = (epochMs) => {
  if (!epochMs) return '';
  const d = new Date(epochMs);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const formatDurationFromMsToHms = (durationMs) => {
  if (!durationMs || durationMs <= 0) return '00:00:00';
  const totalSeconds = Math.floor(durationMs);
  const h = String(Math.floor(totalSeconds / 3600)).padStart(2, '0');
  const m = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, '0');
  const s = String(Math.floor(totalSeconds % 60)).padStart(2, '0');
  return `${h}:${m}:${s}`;
};

const formatDurationLabel = (durationMs) => {
  if (!durationMs || durationMs <= 0) return '0m';
  const totalMinutes = durationMs / (1000 * 60);
  const h = Math.floor(totalMinutes / 60);

  if (h > 0) {
    const m = Math.floor(totalMinutes % 60);
    return `${h}h ${m}m`;
  }

  return `${totalMinutes.toFixed(2)}m`;
};

// Parser bersama (mapTripUtils) -> parsing start_wp_time identik dengan Map & TripHistoryList
const getEpochFromWp = (wp) => getWpTimeEpoch(wp) ?? 0;

export default function GantByTimeChart({
  waypoints = [],
  currentIndex = 0,
  onScrub,
  totalDur,
  isMinimized = false,
  chartMode,
  onChartModeChange
}) {
  const { t } = useLanguage();
  const chartRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [activeSegmentIndex, setActiveSegmentIndex] = useState(null);

  const [showLegend, setShowLegend] = useState(false);
  const { segments, dayMarkers, startTime, endTime, statusTotals, calcTotalMs, durationInDays } = useMemo(() => {
    if (!waypoints || waypoints.length === 0) {
      return {
        segments: [],
        dayMarkers: [],
        startTime: 0,
        endTime: 0,
        calcTotalMs: 0,
        durationInDays: 1,
        statusTotals: { off: 0, idle: 0, moving: 0, overspeed: 0 }
      };
    }

    const startEpoch = getEpochFromWp(waypoints[0]);
    const endEpoch = getEpochFromWp(waypoints[waypoints.length - 1]);

    const totalMsDiff = endEpoch - startEpoch;
    const durationInDays = Math.max(1, totalMsDiff / (1000 * 60 * 60 * 24));

    const safeTotalMs = Math.max(totalMsDiff, 1000);

    const segs = [];
    const markers = [];

    let currentSegment = null;
    const totals = { off: 0, idle: 0, moving: 0, overspeed: 0 };

    let currentDayStr = new Date(startEpoch).toDateString();

    for (let i = 0; i < waypoints.length; i++) {
      const wp = waypoints[i];
      const t1 = getEpochFromWp(wp);
      const wpDayStr = new Date(t1).toDateString();
      if (wpDayStr !== currentDayStr && i > 0) {
        markers.push({
          index: i,
          epoch: t1,
          label: formatDateOnlyGan(t1),
          percentX: ((t1 - startEpoch) / safeTotalMs) * 100
        });
        currentDayStr = wpDayStr;
      }

      if (i === waypoints.length - 1) break;

      const nextWp = waypoints[i + 1];
      const t2 = getEpochFromWp(nextWp);

      const durationForTotals = t2 > t1 ? t2 - t1 : 0;

      let engineRaw = nextWp.engine ?? nextWp.engine_status ?? nextWp.info?.engine_status;
      let engineStatus = engineRaw === false || engineRaw === 0 || engineRaw === '0' ? 'OFF' : String(engineRaw || '').toUpperCase();

      const currentSpeed = Number(nextWp.speed || 0);
      const limitSpeed = Number(nextWp.vehicle?.max_speed || 70);

      const spatialId = Number(nextWp.spatial_id || nextWp.info?.spatial_id || 0);
      const spatialMaxSpeed = Number(nextWp.spatial_max_speed || nextWp.info?.spatial_max_speed || 0);
      const harshEvent = nextWp.harsh_event || nextWp.info?.harsh_event;
      const status = String(nextWp.status || nextWp.info?.status_text || nextWp.type || '').toLowerCase();
      const isStatusOverSpeed = status.includes('over speed') || status.includes('overspeed');
      const isOverSpeedSpatial = nextWp.is_in_retase !== false && spatialId > 0 && spatialMaxSpeed > 0 && currentSpeed > spatialMaxSpeed;

      let color = nextWp.lineColor || '#1d89d5';
      let typeLabel = nextWp.sensorLabel || 'Moving';
      let statusKey = 'moving';

      if (engineStatus === 'OFF' || engineStatus.includes('OFF')) {
        color = '#9e9e9e';
        typeLabel = 'Engine Off';
        statusKey = 'off';
      } else if (engineStatus === 'IDLE' || (engineStatus === 'ON' && currentSpeed === 0)) {
        color = '#FFC107';
        typeLabel = 'Idle';
        statusKey = 'idle';
      } else if (engineStatus === 'ON' || currentSpeed > 0) {
        if (isOverSpeedSpatial) {
          color = '#F39399';
          typeLabel = 'Over Speed (Area)';
          statusKey = 'overspeed';
        } else if (harshEvent) {
          color = '#F44336';
          const harshStr = String(harshEvent).toLowerCase();
          if (harshStr.includes('braking') || harshStr.includes('breaking')) {
            typeLabel = 'Harsh Braking';
          } else if (harshStr.includes('acceleration')) {
            typeLabel = 'Harsh Acceleration';
          } else if (harshStr.includes('cornering')) {
            typeLabel = 'Harsh Cornering';
          } else {
            typeLabel = 'Harsh Event';
          }
          statusKey = 'overspeed';
        } else if (isStatusOverSpeed || currentSpeed > limitSpeed) {
          color = '#F44336';
          typeLabel = 'Over Speed';
          statusKey = 'overspeed';
        }
      } else if (currentSpeed === 0) {
        color = '#FFC107';
        typeLabel = 'Idle';
        statusKey = 'idle';
      }

      totals[statusKey] += durationForTotals;

      if (!currentSegment) {
        currentSegment = {
          type: typeLabel,
          color: color,
          startIdx: i,
          endIdx: i + 1,
          startEpoch: t1,
          endEpoch: Math.max(t2, t1 + 1000)
        };
      } else if (currentSegment.type === typeLabel) {
        currentSegment.endIdx = i + 1;
        currentSegment.endEpoch = Math.max(t2, currentSegment.endEpoch + 1000);
      } else {
        currentSegment.widthPercent = ((currentSegment.endEpoch - currentSegment.startEpoch) / safeTotalMs) * 100;
        currentSegment.leftPercent = ((currentSegment.startEpoch - startEpoch) / safeTotalMs) * 100;
        segs.push(currentSegment);

        currentSegment = {
          type: typeLabel,
          color: color,
          startIdx: i,
          endIdx: i + 1,
          startEpoch: t1,
          endEpoch: Math.max(t2, t1 + 1000)
        };
      }
    }

    if (currentSegment) {
      currentSegment.widthPercent = ((currentSegment.endEpoch - currentSegment.startEpoch) / safeTotalMs) * 100;
      currentSegment.leftPercent = ((currentSegment.startEpoch - startEpoch) / safeTotalMs) * 100;
      segs.push(currentSegment);
    }

    const totalMs = totals.off + totals.idle + totals.moving + totals.overspeed;

    return {
      segments: segs,
      dayMarkers: markers,
      startTime: startEpoch,
      endTime: endEpoch,
      calcTotalMs: totalMs,
      durationInDays: durationInDays,
      statusTotals: totals
    };
  }, [waypoints]);

  const crosshairPercent = useMemo(() => {
    if (!waypoints || waypoints.length === 0) return 0;
    const startEpoch = getEpochFromWp(waypoints[0]);
    const endEpoch = getEpochFromWp(waypoints[waypoints.length - 1]);
    const safeTotalMs = Math.max(endEpoch - startEpoch, 1000);
    const currentEpoch = getEpochFromWp(waypoints[currentIndex]);
    return ((currentEpoch - startEpoch) / safeTotalMs) * 100;
  }, [currentIndex, waypoints]);

  const activeCrosshairSegment = useMemo(() => {
    const currentEpoch = getEpochFromWp(waypoints[currentIndex]);
    return segments.find((seg) => currentEpoch >= seg.startEpoch && currentEpoch <= seg.endEpoch) || null;
  }, [segments, currentIndex, waypoints]);

  const currentWpTime = getEpochFromWp(waypoints[currentIndex]) ? formatDateTimeFull(getEpochFromWp(waypoints[currentIndex])) : '';
  let currentWpType = activeCrosshairSegment ? activeCrosshairSegment.type : '';
  if (currentWpType === 'Normal Route') currentWpType = 'Moving';
  if (currentWpType === 'Engine Off') currentWpType = 'Off';

  const currentWpColor = activeCrosshairSegment ? activeCrosshairSegment.color : '#2196F3';

  useEffect(() => {
    if (!scrollContainerRef.current || !chartRef.current) return;

    const chartWidth = chartRef.current.clientWidth;
    const containerWidth = scrollContainerRef.current.clientWidth;

    const crosshairPixelPos = (crosshairPercent / 100) * chartWidth;
    const currentScrollLeft = scrollContainerRef.current.scrollLeft;

    if (crosshairPixelPos < currentScrollLeft || crosshairPixelPos > currentScrollLeft + containerWidth * 0.9) {
      scrollContainerRef.current.scrollTo({
        left: Math.max(0, crosshairPixelPos - containerWidth / 2),
        behavior: 'smooth'
      });
    }
  }, [currentIndex, crosshairPercent]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (chartRef.current && !chartRef.current.contains(event.target)) {
        setActiveSegmentIndex(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInteraction = (e) => {
    if (!chartRef.current || !onScrub || waypoints.length === 0) return;

    const rect = chartRef.current.getBoundingClientRect();
    let x = e.clientX - rect.left;

    if (x < 0) x = 0;
    if (x > rect.width) x = rect.width;

    const clickedPercent = x / rect.width;
    const safeTotalMs = Math.max(endTime - startTime, 1000);
    const targetEpoch = startTime + clickedPercent * safeTotalMs;

    let closestIdx = 0;
    let minDiff = Infinity;
    for (let i = 0; i < waypoints.length; i++) {
      const diff = Math.abs(getEpochFromWp(waypoints[i]) - targetEpoch);
      if (diff < minDiff) {
        minDiff = diff;
        closestIdx = i;
      }
    }

    let foundSegmentIdx = null;
    for (let i = 0; i < segments.length; i++) {
      if (targetEpoch >= segments[i].startEpoch && targetEpoch <= segments[i].endEpoch) {
        foundSegmentIdx = i;
        break;
      }
    }
    setActiveSegmentIndex(foundSegmentIdx);

    onScrub(closestIdx);
  };

  const handleMouseDown = (e) => {
    handleInteraction(e);
    const onMouseMove = (eMove) => handleInteraction(eMove);
    const onMouseUp = () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };

  const getPercentageString = (valMs) => {
    if (!calcTotalMs || calcTotalMs === 0) return '0.00%';
    const pct = (valMs / calcTotalMs) * 100;
    return `${pct.toFixed(2)}%`;
  };

  if (!waypoints || waypoints.length === 0) return null;
  const chartDynamicWidth = `${Math.max(100, durationInDays * 100 * zoomLevel)}%`;

  const legendItems = [
    { label: 'Off', color: '#9e9e9e', percent: getPercentageString(statusTotals.off), dur: formatDurationLabel(statusTotals.off) },
    { label: 'Idle', color: '#FFC107', percent: getPercentageString(statusTotals.idle), dur: formatDurationLabel(statusTotals.idle) },
    { label: 'Moving', color: '#1d89d5', percent: getPercentageString(statusTotals.moving), dur: formatDurationLabel(statusTotals.moving) },
    {
      label: 'Over Speed',
      color: 'linear-gradient(to bottom, #F44336 50%, #F39399 50%)',
      percent: getPercentageString(statusTotals.overspeed),
      dur: formatDurationLabel(statusTotals.overspeed)
    }
  ];

  const renderLegendContent = () => {
    if (isMinimized) {
      return (
        <Stack spacing={1} sx={{ minWidth: 150 }}>
          {legendItems.map((item, idx) => (
            <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <Box sx={{ width: 4, height: 16, background: item.color, borderRadius: 1, mt: 0.3 }} />
              <Box>
                <Typography variant="caption" sx={{ fontSize: '0.70rem', display: 'block', lineHeight: 1.2 }}>
                  {item.label} <strong style={{ color: '#1e293b' }}>{item.percent}</strong>
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#64748b' }}>
                  {item.dur}
                </Typography>
              </Box>
            </Box>
          ))}
        </Stack>
      );
    }

    return (
      <Grid container spacing={1}>
        {legendItems.map((item, idx) => (
          <Grid item size={{ xs: 6 }} key={idx}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                bgcolor: '#ffffff',
                border: '1px solid #e2e8f0',
                borderRadius: 1,
                py: 0.2,
                px: 0.2,
                position: 'relative',
                overflow: 'hidden',
                boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
              }}
            >
              {/* Left border accent */}
              <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 8, background: item.color }} />

              <Box sx={{ pl: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontSize: 11, color: '#64748b', whiteSpace: 'nowrap' }}>
                  {item.label} <strong style={{ color: '#1e293b' }}>{item.percent}</strong>
                </Typography>
                <Typography sx={{ fontSize: 11, fontWeight: 'bold', color: '#1e293b', whiteSpace: 'nowrap' }}>{item.dur}</Typography>
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <Box sx={{ width: '100%', mt: isMinimized ? 0 : 0.5 }}>
      {!isMinimized && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
          <Box>{renderLegendContent()}</Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: '#f1f5f9', borderRadius: 1, px: 0.5 }}>
              <IconButton size="small" onClick={() => setZoomLevel((prev) => Math.max(1, prev - 1))} disabled={zoomLevel <= 1}>
                <ZoomOutIcon sx={{ fontSize: 18 }} />
              </IconButton>
              <Typography variant="caption" sx={{ minWidth: 24, textAlign: 'center', fontWeight: 'bold' }}>
                {zoomLevel}x
              </Typography>
              <IconButton size="small" onClick={() => setZoomLevel((prev) => Math.min(20, prev + 1))} disabled={zoomLevel >= 20}>
                <ZoomInIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
          </Box>
        </Box>
      )}

      <Box
        ref={scrollContainerRef}
        sx={{
          width: '100%',
          overflowX: 'auto',
          pt: isMinimized ? 2.2 : 2,
          pb: isMinimized ? 0.3 : 3,
          cursor: 'grab',
          '&:active': { cursor: 'grabbing' },
          '&::-webkit-scrollbar': { height: 3 },
          '&::-webkit-scrollbar-thumb': { bgcolor: '#cbd5e1', borderRadius: 2 }
        }}
      >
        <Box
          ref={chartRef}
          onMouseDown={handleMouseDown}
          sx={{
            position: 'relative',
            height: isMinimized ? 8 : 16,
            width: chartDynamicWidth,
            bgcolor: '#e2e8f0',
            borderRadius: '4px',
            cursor: 'pointer',
            border: '1px solid #cbd5e1',
            mt: isMinimized ? 0 : 2
          }}
        >
          {segments.map((seg, idx) => {
            const isActive = activeSegmentIndex === idx;
            let highlightColor = '#383838';
            if (seg.color === '#1d89d5')
              highlightColor = '#04558f'; // Moving
            else if (seg.color === '#FFC107')
              highlightColor = '#ce9b04'; // Idle
            else if (seg.color === '#F44336') highlightColor = '#b1180d'; // Overspeed

            return (
              <Box
                key={idx}
                title={`${seg.type}: ${formatTime(seg.startEpoch)} - ${formatTime(seg.endEpoch)}`}
                sx={{
                  position: 'absolute',
                  left: `${seg.leftPercent}%`,
                  width: `${seg.widthPercent}%`,
                  height: '100%',
                  bgcolor: seg.color,
                  borderTop: isActive ? `1px solid ${highlightColor}` : 'none',
                  borderBottom: isActive ? `1px solid ${highlightColor}` : 'none',
                  opacity: activeSegmentIndex !== null && !isActive ? 0.6 : 1,
                  boxSizing: 'border-box',
                  zIndex: isActive ? 5 : 1,
                  transition: 'all 0.2s'
                }}
              />
            );
          })}

          {dayMarkers.map((marker, idx) => (
            <Box
              key={`marker-${idx}`}
              sx={{
                position: 'absolute',
                left: `${marker.percentX}%`,
                top: isMinimized ? -3 : -10,
                bottom: isMinimized ? -2 : -4,
                width: '1px',
                bgcolor: '#94a3b8',
                zIndex: 0,
                pointerEvents: 'none'
              }}
            >
              {!isMinimized && (
                <Typography
                  variant="caption"
                  sx={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    fontSize: '12px',
                    fontWeight: 'bold',
                    color: '#6c6c6c'
                  }}
                >
                  {marker.label}
                </Typography>
              )}
            </Box>
          ))}

          {/* CROSSHAIR / INDIKATOR POSISI AKTIF */}
          <Box
            sx={{
              position: 'absolute',
              left: `${crosshairPercent}%`,
              top: 0,
              bottom: 0,
              width: isMinimized ? 2 : 4,
              bgcolor: '#0f172a',
              zIndex: 10,
              pointerEvents: 'none',
              transform: 'translateX(-50%)'
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: isMinimized ? -18 : -24,
                left: '50%',
                transform: `translateX(-${5 + crosshairPercent * 0.9}%)`,
                bgcolor: currentWpColor,
                color: '#fff',
                fontSize: isMinimized ? '9px' : '10px',
                px: isMinimized ? 0.6 : 0.8,
                py: isMinimized ? 0.1 : 0.2,
                borderRadius: 1,
                fontWeight: 'bold',
                whiteSpace: 'nowrap',
                boxShadow: '0 2px 4px rgba(0,0,0,0.25)',
                transition: 'transform 0.1s ease-out, background-color 0.2s',
                lineHeight: 1.2
              }}
            >
              {currentWpTime} {currentWpType ? `• ${currentWpType}` : ''}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Date / Time & Zoom Row */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 0.5,
          mt: isMinimized ? 0.2 : 0.5
        }}
      >
        <Typography
          sx={{
            fontSize: isMinimized ? 10.5 : 12,
            fontWeight: 'bold',
            color: '#334155',
            display: 'flex',
            alignItems: 'center',
            gap: 0.4
          }}
        >
          {isMinimized && <AccessTimeIcon sx={{ fontSize: 11, color: '#64748b' }} />}
          {formatDateOnly(startTime)} {formatTime(startTime)}
        </Typography>

        {isMinimized && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                bgcolor: '#f1f5f9',
                borderRadius: 1,
                px: 0.4,
                py: 0.1,
                border: '1px solid #e2e8f0'
              }}
            >
              <IconButton
                size="small"
                onClick={() => setZoomLevel((prev) => Math.max(1, prev - 1))}
                disabled={zoomLevel <= 1}
                sx={{ p: 0.2, width: 18, height: 18 }}
              >
                <ZoomOutIcon sx={{ fontSize: 12 }} />
              </IconButton>
              <Typography sx={{ fontSize: 9.5, minWidth: 20, textAlign: 'center', fontWeight: 'bold', color: '#475569' }}>
                {zoomLevel}x
              </Typography>
              <IconButton
                size="small"
                onClick={() => setZoomLevel((prev) => Math.min(20, prev + 1))}
                disabled={zoomLevel >= 20}
                sx={{ p: 0.2, width: 18, height: 18 }}
              >
                <ZoomInIcon sx={{ fontSize: 12 }} />
              </IconButton>
            </Box>

            <Box sx={{ position: 'relative' }}>
              <IconButton size="small" onClick={() => setShowLegend(!showLegend)} sx={{ p: 0.2, width: 20, height: 20 }}>
                <InfoOutlinedIcon sx={{ fontSize: 14, color: showLegend ? '#1d89d5' : '#64748b' }} />
              </IconButton>

              {showLegend && (
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: '100%',
                    right: 0,
                    mb: 0.5,
                    bgcolor: 'white',
                    border: '1px solid #cbd5e1',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    p: 1,
                    borderRadius: 1.5,
                    zIndex: 9999,
                    minWidth: 180
                  }}
                >
                  {renderLegendContent()}
                  <Box sx={{ display: 'flex', mt: 0.8, pt: 0.5, borderTop: '1px solid #f1f5f9' }}>
                    <Typography sx={{ fontSize: 10, color: '#64748b' }}>{t('mapsTracking.totalDuration', 'Total :')}</Typography>
                    <Typography sx={{ fontSize: 10, ml: 0.5, fontWeight: 'bold', color: '#1e293b' }}>
                      {formatDurationFromMsToHms(totalDur)}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        )}

        <Typography
          sx={{
            fontSize: isMinimized ? 10.5 : 12,
            fontWeight: 'bold',
            color: '#334155',
            display: 'flex',
            alignItems: 'center',
            gap: 0.4
          }}
        >
          {formatDateOnly(endTime)} {formatTime(endTime)}
          {isMinimized && <AccessTimeIcon sx={{ fontSize: 11, color: '#64748b' }} />}
        </Typography>
      </Box>
    </Box>
  );
}
